/**
 * Swipe View — analytics emission seam (SMASH-1853).
 *
 * Spec §8: shared event schema, per-plugin route (§9.3 — "a plugin installed
 * on its own must work with no reference to another plugin's endpoint").
 * The shared route itself is a SEPARATE ticket, SMASH-1852, and does not
 * exist yet. This module is the single seam every `swipeview_*` event flows
 * through, so wiring the real transport later is a one-file change.
 *
 * Requirements this module enforces regardless of transport (§8):
 *   - every event carries a schema version and the post identity (§9.2)
 *   - client-side throttle of at most one event per 250ms PER EVENT TYPE
 *   - a failed send is dropped silently — never retries, never blocks playback
 */

export const SWIPEVIEW_ANALYTICS_SCHEMA_VERSION = 1;
const THROTTLE_MS = 250;

const lastSentAt = new Map();

/**
 * TODO(SMASH-1852): replace this body with a real POST to the shared
 * analytics route once it exists. Until then this is intentionally a no-op
 * seam (dev-only console signal) — inventing an endpoint here would be
 * exactly the kind of "load-bearing cross-plugin dependency" AgDR-0500
 * rules out (§9.3: shared schema, per-plugin route, no shared runtime dep).
 */
function sendSwipeAnalyticsEvent(eventName, payload) {
    try {
        if (typeof window !== 'undefined' && window.sbtt_debug_analytics) {
            // eslint-disable-next-line no-console
            console.debug('[swipeview analytics]', eventName, payload);
        }
    } catch (e) {
        // A failed send must never retry-storm or block playback (§8).
    }
}

/**
 * @param {string} eventName  One of the `swipeview_*` names in spec §8.
 * @param {object} payload    Event-specific fields (index, total, reason, …).
 * @param {string|number|null} postId  The active post's identity (§9.2); may
 *   be null for events that are not post-scoped.
 */
export function emitSwipeAnalytics(eventName, payload, postId) {
    const now = Date.now();
    const last = lastSentAt.get(eventName) || 0;
    if (now - last < THROTTLE_MS) return;
    lastSentAt.set(eventName, now);

    sendSwipeAnalyticsEvent(eventName, {
        ...payload,
        schemaVersion: SWIPEVIEW_ANALYTICS_SCHEMA_VERSION,
        postId: postId ?? null,
    });
}

/** Test/dev helper: clears the throttle map between cases. */
export function _resetSwipeAnalyticsThrottle() {
    lastSentAt.clear();
}
